// Angular
import { AfterViewInit, Directive, OnDestroy } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../reducers';
import { currentUser, isLoggedIn } from '../../../auth';
import { Subscription } from 'rxjs';

export let interval: any = 0;

export function stop() {
  if (interval) {
    clearInterval(interval);
    interval = 0;
  }
}

@Directive({
  selector: '[ktTwixor]',
  exportAs: 'ktTwixor',
})
export class ChatCountTwixorDirective implements AfterViewInit, OnDestroy {
  private connector: any;
  currentUser;
  lastValueOfChat = 0;
  userInfo;
  newNotificationUrl;
  snoozeSeconds = 0;
  userLoggedOut = null;
  private subscriptions: Subscription[] = [];
  private isDestroyed = false;

  constructor(private store: Store<AppState>) {
    const currentUserSub = this.store.pipe(select(currentUser)).subscribe((res) => {
      this.userInfo = res;
      this.currentUser = res;
    });
    const isLoggedInSub = this.store.pipe(select(isLoggedIn)).subscribe((res) => {
      this.userLoggedOut = !res;
    });
    this.subscriptions.push(currentUserSub, isLoggedInSub);
  }

  toDataURL(url, callback) {
    if (!url || url === 'null' || url === 'undefined') {
      console.warn('Invalid URL provided to toDataURL:', url);
      callback(null);
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      if (xhr.status === 200 && xhr.response) {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result) {
            callback(reader.result);
          } else {
            console.error('Failed to convert response to data URL');
            callback(null);
          }
        };
        reader.onerror = () => {
          console.error('FileReader error');
          callback(null);
        };
        reader.readAsDataURL(xhr.response);
      } else {
        console.error(`Failed to load sound: HTTP ${xhr.status}`);
        callback(null);
      }
    };
    xhr.onerror = () => {
      console.error('XHR request failed for URL:', url);
      callback(null);
    };
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
  }

  play() {
    if (this.isDestroyed) return;

    if (this.userLoggedOut) stop();
    if (!this.userInfo?.notificationSettings?.mute && !this.userLoggedOut) {
      const repeatedSoundUrl = this.userInfo?.notificationSettings?.repeated_sound_url
        ? this.userInfo.notificationSettings.repeated_sound_url
        : './assets/sounds/Notification.mp3';
      const repeated_sound_times = this.userInfo?.notificationSettings?.repeated_sound_times
        ? this.userInfo.notificationSettings.repeated_sound_times
        : 1;
      let src = '';
      this.toDataURL(repeatedSoundUrl, (res) => {
        if (!res) {
          console.warn('Failed to load repeated sound, skipping playback');
          return;
        }
        src = res;
        let i = 1;
        const audio = new Audio(src);
        audio.addEventListener(
          'ended',
          function () {
            this.currentTime = 0;
            if (i < repeated_sound_times) {
              audio.play().catch((err) => console.error('Error playing repeated sound:', err));
              i++;
            }
          },
          false,
        );
        audio.play().catch((err) => console.error('Error starting repeated sound:', err));
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.currentUser && this.currentUser.agent) {
      setTimeout(() => {
        if (this.isDestroyed) return;

        // Check if Twixor is available
        if (typeof Twixor === 'undefined' || !Twixor.Connector) {
          console.error('Twixor library not loaded');
          return;
        }

        this.connector = Twixor.Connector;
        this.connector.on('countChanged', (counts) => {
          if (this.isDestroyed) return;

          if (counts.total === 0) {
            stop();
          }
          const seconds = this.userInfo?.notificationSettings?.seconds
            ? this.userInfo.notificationSettings.seconds
            : 60;
          this.snoozeSeconds = seconds * 1000;
          this.newNotificationUrl = this.userInfo?.notificationSettings?.notification_sound_url
            ? this.userInfo.notificationSettings.notification_sound_url
            : './assets/sounds/newNotification.mp3';
          if (!this.userInfo?.notificationSettings?.mute) {
            if (counts.total !== 0 && counts.total !== this.lastValueOfChat) {
              let src: string;
              this.toDataURL(this.newNotificationUrl, (res) => {
                if (!res) {
                  console.warn('Failed to load notification sound, skipping playback');
                  return;
                }
                src = res;
                const audio = new Audio(src);
                audio
                  .play()
                  .catch((err) => console.error('Error playing notification sound:', err));
              });

              // Clear existing interval before setting new one
              if (interval) {
                clearInterval(interval);
              }
              interval = setInterval(() => {
                if (!this.isDestroyed) {
                  this.play();
                }
              }, this.snoozeSeconds);
            }
          }

          // Update DOM elements with null checks
          const chatCountElement = document.getElementById('chatCount');
          if (chatCountElement) {
            // tslint:disable-next-line:no-bitwise
            chatCountElement.textContent = String(counts.total | 0);
          }

          const badgeElement = document.getElementById('chatCountBadge');
          if (badgeElement) {
            badgeElement.style.display = 'flex';
          }

          this.lastValueOfChat = counts.total;
        });

        this.connector.on('sessionExpired', () => {
          // Intentional no-op: session expiration handled elsewhere
          console.warn('Twixor session expired');
        });

        // Add error handler for connector
        if (this.connector.on) {
          this.connector.on('error', (error) => {
            console.error('Twixor connector error:', error);
          });
        }

        setTimeout(() => {
          if (!this.isDestroyed && this.connector) {
            const token = localStorage.getItem('twixor_token');
            if (token) {
              this.connector.connect(`${environment.ENGAGE_BASE}`, token);
            } else {
              console.warn('No twixor_token found in localStorage');
            }
          }
        }, 3000);
      });
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;

    // Clean up subscriptions
    this.subscriptions.forEach((sub) => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });

    // Stop interval
    stop();

    // Clean up connector if it has disconnect method
    if (this.connector && typeof this.connector.disconnect === 'function') {
      this.connector.disconnect();
    }

    // Clean up event listeners if possible
    if (this.connector && typeof this.connector.off === 'function') {
      this.connector.off('countChanged');
      this.connector.off('sessionExpired');
      this.connector.off('error');
    }

    this.connector = null;
  }
}
